import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@commenda/kakashi';
import { useCallback, useRef, useState } from 'react';
import {
  FilingCalendarPreviewData,
  IndirectTaxFilingCalendarPreviewModalProps,
  MonthYearValue,
} from '../../types';
import { FilingCalendarPreviewContent } from '../FilingCalendarPreviewContent/FilingCalendarPreviewContent';
import { FilingCalendarPreviewForm } from './FilingCalendarPreviewForm';

export function IndirectTaxFilingCalendarPreviewModal({
  isOpen,
  onClose,
  corporationId,
  initialTaxPeriodStart,
  onSuccess,
}: IndirectTaxFilingCalendarPreviewModalProps) {
  const [previewData, setPreviewData] = useState<
    FilingCalendarPreviewData | undefined
  >();
  const [isLoading, setIsLoading] = useState(false);
  const [taxPeriodStart, setTaxPeriodStart] = useState<MonthYearValue | null>(
    initialTaxPeriodStart ?? null
  );
  const [taxPeriodEnd, setTaxPeriodEnd] = useState<MonthYearValue | null>(null);
  const [showProposedCalendar, setShowProposedCalendar] = useState(false);
  const refetchPreviewRef = useRef<(() => void) | null>(null);
  const handleFilingDeleted = useCallback(() => {
    refetchPreviewRef.current?.();
  }, []);

  const handleSuccess = () => {
    onSuccess?.();
    onClose();
  };

  const handleTaxPeriodChange = (
    start: MonthYearValue | null,
    end: MonthYearValue | null
  ) => {
    setTaxPeriodStart(start);
    setTaxPeriodEnd(end);
  };

  const handlePreviewGenerated = (generated: boolean) => {
    setShowProposedCalendar(generated);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="min-w-[90vw] p-none h-[90vh] flex flex-col"
        hideCloseIcon={false}
      >
        <DialogHeader className="border-b border-border-primary p-3xl">
          <DialogTitle>Generate filing calendar</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Left panel — form */}
          <div className="w-[400px] border-r border-border-primary p-3xl flex flex-col">
            <FilingCalendarPreviewForm
              corporationId={corporationId}
              initialTaxPeriodStart={initialTaxPeriodStart}
              onSuccess={handleSuccess}
              onPreviewDataChange={setPreviewData}
              onLoadingChange={setIsLoading}
              onTaxPeriodChange={handleTaxPeriodChange}
              onPreviewGenerated={handlePreviewGenerated}
              registerRefetchPreview={(fn) => {
                refetchPreviewRef.current = fn;
              }}
            />
          </div>

          {/* Right Panel - Calendar Preview */}
          <div className="flex-1 overflow-auto">
            <FilingCalendarPreviewContent
              previewData={previewData}
              isLoading={isLoading}
              showOldCalendar
              taxPeriodStart={taxPeriodStart}
              taxPeriodEnd={taxPeriodEnd}
              showProposedCalendar={showProposedCalendar}
              onFilingDeleted={handleFilingDeleted}
              corporationId={corporationId}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
